import { PlainDate } from "@kiss-solutions/plaindate";
import * as yup from "yup";
import { dataGridDateValueGetter } from "../components/utils/listValueGetter";
import { i18_EDITPROJEKT, i18_EDITTERMIN, i18_KUNDE, i18_LIST, i18_TRANSLATION } from "../i18nReferences";
import { isValidDate } from "../store/utils/dateHelper";
import { pick } from "../store/utils/lodash-replacement";
import { transformNoNumberToUndefined } from "../store/utils/yupSchema";
import { formatDateTime, toDateString, toLocalDateTimeStringInIsoFormat } from "./date";
import { isAnzahlung } from "./einnahme";
import {
    TERMINSTATI_MAP,
    TERMINSTATUS_AUSGEFALLEN,
    TERMINSTATUS_ERLEDIGT,
    TERMINSTATUS_OFFEN,
    TERMINSTATUS_VORGEMERKT,
} from "./terminStatus";
import { TERMINTYPEN_DEPOSIT } from "./termintyp";

export const isTerminForArtist = (termin, artistId) =>
    (termin.ArtistID === artistId && !termin.AttArtistID) || termin.AttArtistID === artistId;
export const isTerminForArtistList = (termin, artistIdList) =>
    (artistIdList.includes(termin.ArtistID) && !termin.AttArtistID) ||
    (termin.AttArtistID && artistIdList.includes(termin.AttArtistID));

export const DEPOSIT_MEETING_TEXT_PART = "Termine+AZ";
export const isDepositMeeting = (termin) => termin.Betreff && termin.Betreff.indexOf(DEPOSIT_MEETING_TEXT_PART) >= 0;

export const createTerminSchema = () => {
    return yup.object().shape({
        Start: yup.date().min(new Date("1970-01-01")).required(),
        DatBis: yup.date().min(yup.ref("Start"), { key: "YUP.ERROR.START_AFTER_END" }).required(),
        Betreff: yup.string().max(60).nullable(true),
        ArtistID: yup
            .number()
            .when(["$artistIdListWrite"], ([artistIdListWrite], schema) =>
                schema.oneOf(artistIdListWrite, { key: "YUP.ERROR.KEINE_RECHTE" })
            ),
    });
};

export const isGespeicherterTermin = (terminId) => terminId > 0;

export const getAnzahlungsEinnahmen = (termin) =>
    termin &&
    termin.einnahmen &&
    TERMINTYPEN_DEPOSIT.includes(termin.Typ) &&
    termin.einnahmen
        .filter((e) => !isAnzahlung(e))
        .map((t) => t.Betrag)
        .reduce((sum, amt) => sum + Math.round(amt * 100) / 100, 0);

export const getAnzahlungsVerrechnungen = (termin) =>
    termin &&
    termin.einnahmen &&
    !TERMINTYPEN_DEPOSIT.includes(termin.Typ) &&
    termin.einnahmen
        .filter((e) => isAnzahlung(e))
        .map((t) => t.Betrag)
        .reduce((sum, amt) => sum + Math.round(amt * 100) / 100, 0);

export const convertTerminToSchema = (termin) => ({
    ...pick(termin, ["ArtistID", "Typ", "TrmStat", "Ganztags", "Betreff", "Bemerkung", "TatortID", "KundID", "_createdTS", "_createdID", "ProjektID", "TerminID", "BlockerID"]),
    Start: new Date(termin.Start),
    DatBis: new Date(termin.DatBis),
});

export const convertTerminSchemaToTermin = (termin) => ({
    ...termin,
    Start: isValidDate(termin.Start) ? toLocalDateTimeStringInIsoFormat(termin.Start) : undefined,
    DatBis: isValidDate(termin.DatBis) ? toLocalDateTimeStringInIsoFormat(termin.DatBis) : undefined,
    KundID: termin.KundID || null,
    ProjektID: termin.ProjektID || null,
});

export const createHolidayParams = (termin) => {
    if (!termin.Start || (typeof termin.Start !== "string" && !isValidDate(termin.Start))) {
        return undefined;
    }

    return {
        artistId: termin.ArtistID,
        start: new PlainDate(termin.Start).toDateString(),
        end: new PlainDate(termin.Start).toDateString(),
    };
};

export const createCalculatorSchema = () => {
    return yup.object().shape({
        stunden: yup.number().transform(transformNoNumberToUndefined),
        minuten: yup.number().transform(transformNoNumberToUndefined).max(60).min(-60),
        preis: yup.number().transform(transformNoNumberToUndefined).min(0).required(),
    });
};

export const getTranslatedTerminstatusSelections = (t) =>
    Object.keys(TERMINSTATI_MAP).map((key) => ({ id: key, text: t(key, { ns: i18_TRANSLATION }) }));
export const getTranslatedTerminStatusMap = (t) =>
    Object.keys(TERMINSTATI_MAP).reduce((pv, cv) => {
        pv[TERMINSTATI_MAP[cv]] = t(cv, { ns: i18_TRANSLATION });
        return pv;
    }, {});

export const getDuration = (termin) =>
    termin && (new Date(termin.DatBis).getTime() - new Date(termin.Start).getTime()) / 1000 / 60;

export const getTerminListColumnsForKunde = ({
    t,
    artistMap,
    translatedProjektStatusMap,
    translatedTerminStatusMap,
    timezone,
    locale,
    termintypMap,
}) => [
    { field: "TerminID", type: "number", headerName: t("LIST.TERMINID_LABEL", { ns: i18_LIST }) },
    {
        field: "ArtistID",
        headerName: "Artist",
        valueGetter: (value) => value && artistMap && artistMap[value].Kuerzel,
    },
    { field: "ProjName", headerName: t("EDIT_PROJEKT.PROJNAME_LABEL", { ns: i18_EDITPROJEKT }), minWidth: 250 },
    {
        field: "ProjStat",
        headerName: t("TERMIN_LIST.PROJEKT_STATUS_LABEL", { ns: i18_LIST }),
        valueGetter: (value) => value !== undefined && translatedProjektStatusMap && translatedProjektStatusMap[value],
    },
    {
        field: "Typ",
        headerName: t("APPOINTMENT.TERMINTYP_LABEL", { ns: i18_EDITTERMIN }),
        valueGetter: (value) => value !== undefined && termintypMap && termintypMap[value].Termintyp,
    },
    { field: "Start", headerName: t("EDIT_TERMIN.START_LABEL", { ns: i18_TRANSLATION }), type: "dateTime", valueGetter: dataGridDateValueGetter, valueFormatter: value => formatDateTime(value, locale, timezone), minWidth: 150 },
    { field: "Dauer", type: "number", headerName: t("EDIT_TERMIN.DURATION_LABEL", { ns: i18_TRANSLATION }) },
    {
        field: "TrmStat",
        headerName: t("APPOINTMENT.TERMINSTATUS_LABEL", { ns: i18_EDITTERMIN }),
        valueGetter: (value) => value !== undefined && translatedTerminStatusMap && translatedTerminStatusMap[value],
    },

];

export const getTerminListColumns = ({
    t,
    artistMap,
    translatedProjektStatusMap,
    translatedTerminStatusMap,
    tatortMap,
    termintypMap,
    translatedMsgPrefMap,
    timezone,
    locale,
}) => [
    { field: "KundID", type: "number", headerName: t("LIST.KUNDID_LABEL", { ns: i18_LIST }) },
    { field: "ProjektID", type: "number", headerName: t("LIST.PROJEKTID_LABEL", { ns: i18_LIST }) },
    { field: "TerminID", type: "number", headerName: t("LIST.TERMINID_LABEL", { ns: i18_LIST }) },
    { field: "Vorname", headerName: t("EDIT_KUNDE.VORNAME_LABEL", { ns: i18_KUNDE }) },
    { field: "Nachname", headerName: t("EDIT_KUNDE.NACHNAME_LABEL", { ns: i18_KUNDE }) },
    { field: "gesperrt", type: "boolean", headerName: t("EDIT_KUNDE.GESPERRT_LABEL", { ns: i18_KUNDE }) },
    { field: "KannKuFri", type: "boolean", headerName: t("EDIT_KUNDE.KANN_KURZFRISTIG_LABEL", { ns: i18_KUNDE }) },
    {
        field: "MsgPref",
        headerName: t("EDIT_KUNDE.MSGPREF_LABEL", { ns: i18_KUNDE }),
        valueGetter: (value) => value !== undefined && translatedMsgPrefMap && translatedMsgPrefMap[value],
    },
    { field: "Mobil", headerName: t("EDIT_KUNDE.MOBILE_LABEL", { ns: i18_KUNDE }), minWidth: 150 },
    { field: "Email", headerName: t("EDIT_KUNDE.EMAIL_LABEL", { ns: i18_KUNDE }), minWidth: 200 },
    { field: "ProjName", headerName: t("EDIT_PROJEKT.PROJNAME_LABEL", { ns: i18_EDITPROJEKT }), minWidth: 250 },
    {
        field: "ProjektArtistID",
        headerName: t("TERMIN_LIST.PROJEKT_ARTIST_LABEL", { ns: i18_LIST }),
        valueGetter: (value) => value && artistMap && artistMap[value].Kuerzel,
    },
    {
        field: "ProjStat",
        headerName: t("TERMIN_LIST.PROJEKT_STATUS_LABEL", { ns: i18_LIST }),
        valueGetter: (value) => value !== undefined && translatedProjektStatusMap && translatedProjektStatusMap[value],
    },
    {
        field: "AnzAgreed",
        headerName: t("EDIT_PROJEKT.VEREINBARTE_ANZAHLUNG_LABEL", { ns: i18_EDITPROJEKT }),
        type: "number",
    },
    {
        field: "AnzStatus",
        headerName: t("EDIT_PROJEKT.ANZAHLUNGSSTATUS_LABEL", { ns: i18_EDITPROJEKT }),
        type: "number",
    },
    {
        field: "DiffAnzSaldo",
        headerName: t("PROJEKT_LIST.ANZAHLUNG_UNVOLLSTAENDIG_LABEL", { ns: i18_EDITPROJEKT }),
        type: "boolean",
        valueGetter: (value, row) => +row.AnzAgreed > (+row.AnzStatus || 0),
    },
    {
        field: "AnzInfo",
        headerName: t("EDIT_PROJEKT.BEMERKUNG_VEREINBARTE_ANZAHLUNG_LABEL", { ns: i18_EDITPROJEKT }),
        type: "string",
    },
    {
        field: "Preis",
        headerName: t("EDIT_PROJEKT.PREIS_LABEL", { ns: i18_EDITPROJEKT }),
        minWidth: 150,
    },
    {
        field: "ProjektBemerkung",
        headerName: t("EDIT_PROJEKT.BEMERKUNG_LABEL", { ns: i18_EDITPROJEKT }),
        type: "string",
        minWidth: 250,
        valueGetter: (value) => value && value.replaceAll("\n", " "),
    },
    {
        field: "TatortID",
        headerName: "Location",
        type: "string",
        minWidth: 150,
        valueGetter: (value) => value && tatortMap && tatortMap[value].Ort,
    },
    {
        field: "isLocationDeviation",
        headerName: t("TERMIN_LIST.IS_LOCATION_DEVIATION", { ns: i18_LIST }),
        type: "boolean",
    },
    {
        field: "ArtistID",
        headerName: "Artist",
        valueGetter: (value) => value && artistMap && artistMap[value].Kuerzel,
    },
    {
        field: "Typ",
        headerName: t("APPOINTMENT.TERMINTYP_LABEL", { ns: i18_EDITTERMIN }),
        valueGetter: (value) => value !== undefined && termintypMap && termintypMap[value].Termintyp,
    },
    {
        field: "TrmStat",
        headerName: t("APPOINTMENT.TERMINSTATUS_LABEL", { ns: i18_EDITTERMIN }),
        valueGetter: (value) => value !== undefined && translatedTerminStatusMap && translatedTerminStatusMap[value],
    },
    {
        field: "Start",
        headerName: t("EDIT_TERMIN.START_LABEL", { ns: i18_TRANSLATION }),
        type: "dateTime",
        valueGetter: dataGridDateValueGetter,
        valueformatter: value => formatDateTime(value, locale),
        minWidth: 150,
    },
    {
        field: "DatBis",
        headerName: t("EDIT_TERMIN.END_LABEL", { ns: i18_TRANSLATION }),
        type: "dateTime",
        valueGetter: dataGridDateValueGetter,
        valueFormatter: value => formatDateTime(value, locale),
        minWidth: 150,
    },
    {
        field: "Dauer",
        type: "number",
        headerName: t("EDIT_TERMIN.DURATION_LABEL", { ns: i18_TRANSLATION }),
    },
    { field: "Betreff", headerName: t("APPOINTMENT.BETREFF_LABEL", { ns: i18_EDITTERMIN }), minWidth: 150 },
    {
        field: "maxAnamnese",
        headerName: t("TERMIN_LIST.ANAMNESE_LABEL", { ns: i18_LIST }),
        valueGetter: (value) => (value ? formatDateTime(new Date(value), locale, timezone) : null),
        minWidth: 150,
    },
    {
        field: "fehltGueltigeAnamnese",
        type: "boolean",
        headerName: t("TERMIN_LIST.ANAMNESE_FEHLT_LABEL", { ns: i18_LIST }),
    },
    {
        field: "maxDatenschutz",
        headerName: t("TERMIN_LIST.DATENSCHUTZ_LABEL", { ns: i18_LIST }),
        type: "dateTime",
        valueGetter: dataGridDateValueGetter,
        valueFormatter: value => formatDateTime(value, locale, timezone),
        minWidth: 150,
    },
    {
        field: "maxEinverstaendnis",
        headerName: t("TERMIN_LIST.EINVERSTAENDNIS_LABEL", { ns: i18_LIST }),
        type: "dateTime",
        valueGetter: dataGridDateValueGetter,
        valueFormatter: value => formatDateTime(value, locale, timezone),
        minWidth: 150,
    },
    {
        field: "maxElternEve",
        headerName: t("TERMIN_LIST.ELTERN_EVE_LABEL", { ns: i18_LIST }),
        type: "dateTime",
        valueGetter: dataGridDateValueGetter,
        valueFormatter: value => formatDateTime(value, locale, timezone),
        minWidth: 150,
    },
    {
        field: "maxTerminDoku",
        headerName: t("TERMIN_LIST.TERMINDOKU_LABEL", { ns: i18_LIST }),
        type: "dateTime",
        valueGetter: dataGridDateValueGetter,
        valueFormatter: value => formatDateTime(value, locale, timezone),
        minWidth: 150,
    },
    {
        field: "isDokuFehlerhaft",
        type: "boolean",
        headerName: t("TERMIN_LIST.DOKU_FEHLER_LABEL", { ns: i18_LIST }),
    },
];

export const getColorForTypAndTrmStat = (termintyp, trmStat) => {
    switch (trmStat) {
        case TERMINSTATUS_OFFEN:
            return "#" + termintyp.FarbeOffen;
        case TERMINSTATUS_AUSGEFALLEN:
            return "#" + termintyp.FarbeCancel;
        case TERMINSTATUS_VORGEMERKT:
            return "#" + termintyp.FarbePending;
        case TERMINSTATUS_ERLEDIGT:
            return "#" + termintyp.FarbeErledigt;
        default:
            return "#f45342";
    }
};

export const isLocationNeeded = ({ Start, DatBis, KundID }) =>
    Start &&
    DatBis &&
    !!KundID &&
    toDateString(Start) === toDateString(DatBis);


export const isBlockerTermin = (termin) => termin?.BlockerID != null
